// Settings-history rows (GET /api/cold-chain/points/:id/versions) in the user's words. Pure.
// Every t() is a literal call so the i18n guard can see the key; never t(map[k]).
import { formatRange, formatTemp } from './coldChainModel';

const DASH = '—';
const EXTRA_KEYS = ['humidityKeys', 'humidityScale', 'doorKeys', 'batteryKeys', 'batteryScale'];

function sourceLabel(family, t) {
  if (family === 'eye') return t('coldChain.source.eye', 'EYE Sensor');
  if (family === 'ble') return t('coldChain.source.ble', 'BLE temperature logger');
  if (family === 'wired') return t('coldChain.source.wired', 'Wired probe');
  if (family === 'custom') return t('coldChain.source.custom', 'Custom keys (expert)');
  return DASH;
}

// 'eye:1' → "EYE Sensor 1"; 'wired' / 'custom' carry no slot.
function sensorText(source, t) {
  if (typeof source !== 'string' || !source) return DASH;
  const [family, slot] = source.split(':');
  const label = sourceLabel(family, t);
  return slot ? `${label} ${slot}` : label;
}

function offsetText(v) {
  if (typeof v !== 'number') return DASH;
  const abs = Number(Math.abs(v).toFixed(2));
  return `${v < 0 ? '-' : '+'}${abs} °C`;
}

const plain = (v) => (v === null || v === undefined || v === '' ? DASH : String(v));
const listText = (v) => (Array.isArray(v) ? (v.length ? v.join(', ') : DASH) : plain(v));

function fieldLabel(field, t) {
  if (field === 'name') return t('coldChain.history.field.name', 'Name');
  if (field === 'locationDetail') return t('coldChain.history.field.locationDetail', 'Location');
  if (field === 'alarmDelayMinutes') return t('coldChain.history.field.alarmDelayMinutes', 'Alarm delay (min)');
  if (field === 'noDataMinutes') return t('coldChain.history.field.noDataMinutes', 'No-data limit (min)');
  if (field === 'calibrationReference') return t('coldChain.history.field.calibrationReference', 'Calibrated against');
  if (field === 'calibratedOn') return t('coldChain.history.field.calibratedOn', 'Calibrated on');
  if (field === 'calibrationDueOn') return t('coldChain.history.field.calibrationDueOn', 'Calibration due');
  return field;
}

export function changeLines(entry, t) {
  const changes = entry?.changes ?? {};
  const has = (k) => k in changes;
  const side = (k, s) => (has(k) ? changes[k][s] : null);
  const lines = [];
  const arrow = (label, from, to) => `${label} ${from} → ${to}`;
  const simple = (f) => arrow(fieldLabel(f, t), plain(changes[f].from), plain(changes[f].to));

  for (const f of ['name', 'locationDetail']) if (has(f)) lines.push(simple(f));
  if (has('tempSource')) {
    lines.push(arrow(t('coldChain.history.field.sensor', 'Sensor'), sensorText(changes.tempSource.from, t), sensorText(changes.tempSource.to, t)));
  } else if (has('tempKeys') || has('tempScale')) {
    const text = (s) => [has('tempKeys') ? listText(changes.tempKeys[s]) : null, has('tempScale') ? plain(changes.tempScale[s]) : null].filter((x) => x !== null).join(' ');
    lines.push(arrow(t('coldChain.history.field.sensorKeys', 'Sensor keys'), text('from'), text('to')));
  }
  if (has('tempOffset')) {
    lines.push(arrow(t('coldChain.history.field.tempOffset', 'Offset'), offsetText(changes.tempOffset.from), offsetText(changes.tempOffset.to)));
  }
  if (has('rangeMin') && has('rangeMax')) {
    const range = (s) => formatRange(side('rangeMin', s), side('rangeMax', s));
    lines.push(arrow(t('coldChain.history.field.range', 'Range'), range('from'), range('to')));
  } else if (has('rangeMin')) {
    lines.push(arrow(t('coldChain.history.field.rangeMin', 'Range min'), formatTemp(changes.rangeMin.from), formatTemp(changes.rangeMin.to)));
  } else if (has('rangeMax')) {
    lines.push(arrow(t('coldChain.history.field.rangeMax', 'Range max'), formatTemp(changes.rangeMax.from), formatTemp(changes.rangeMax.to)));
  }
  for (const f of ['alarmDelayMinutes', 'noDataMinutes']) if (has(f)) lines.push(simple(f));
  if (has('enabled')) {
    const on = t('coldChain.history.on', 'on');
    const off = t('coldChain.history.off', 'off');
    lines.push(arrow(t('coldChain.history.field.enabled', 'Monitoring'), changes.enabled.from ? on : off, changes.enabled.to ? on : off));
  }
  for (const f of ['calibrationReference', 'calibratedOn', 'calibrationDueOn']) if (has(f)) lines.push(simple(f));
  if (EXTRA_KEYS.some(has)) lines.push(t('coldChain.history.field.extraKeys', 'Extra keys'));

  if (!lines.length && Array.isArray(entry?.changedFields) && entry.changedFields.includes('created')) {
    lines.push(t('coldChain.history.created', 'Created'));
  }
  return lines;
}

export function entryTitle(entry, t) {
  return entry?.kind === 'archive' ? t('coldChain.history.archived', 'Archived') : `v${entry?.versionNo}`;
}

export function assumedNote(entry, t) {
  return entry?.provenance === 'assumed'
    ? t('coldChain.history.assumed', 'Settings before the history feature are assumed from the values at the time of upgrade.')
    : null;
}
